import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MediaPreview, MediaThumb } from "@/components/common/MediaPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { query } from "@/services/services";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Panel, PanelHeader } from "@/components/common/Panel";
import { formatDate, fromNow, initials } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users } from "lucide-react";
import { FinanceWorkspace } from "@/components/finance/FinanceWorkspace";
import { useFinanceTotals, money as ledgerMoney } from "@/components/finance/financeApi";

const TABS = ["Overview", "Matters", "Documents", "Communication", "Billing", "Contacts", "Notes", "Timeline"];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { db, fileUrl } = useApp();
  const financeTotals = useFinanceTotals({ clientId: id });
  const [preview, setPreview] = useState(null);
  const c = query.client(db, id);
  if (!c) return <EmptyState icon={Users} title="Client not found" actionLabel="Back to Clients" onAction={() => navigate("/clients")} />;

  const matters = query.mattersForClient(db, id);
  const docs = db.documents.filter((d) => d.clientId === id);

  return (
    <div>
      <div className="mb-2 text-xs text-ink-muted"><button onClick={() => navigate("/clients")} className="hover:text-copper">Clients</button> / {c.name}</div>
      <div className="flex items-center gap-4 border-b border-line pb-5">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-copper text-lg font-semibold text-white">{initials(c.name)}</div>
        <div className="flex-1">
          <h1 className="font-display text-3xl font-semibold text-ink">{c.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-2"><span>{c.type}</span><span>·</span><span>{c.mobile}</span><span>·</span><span>{c.email}</span><StatusBadge status={c.status} /></div>
        </div>
      </div>

      <Tabs defaultValue="Overview" className="mt-6">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
          {TABS.map((t) => <TabsTrigger key={t} value={t} className="rounded-lg px-3 py-1.5 text-sm data-[state=active]:bg-card data-[state=active]:text-copper data-[state=active]:shadow-none border border-transparent data-[state=active]:border-line">{t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="Overview" className="mt-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel className="p-4 lg:col-span-2">
              <h3 className="font-display text-lg font-semibold text-ink">Client Information</h3>
              <div className="mt-3 grid grid-cols-2 gap-y-2.5 text-sm">
                <Info k="Type" v={c.type} /><Info k="Mobile" v={c.mobile} /><Info k="Email" v={c.email} /><Info k="Address" v={c.address} /><Info k="Relationship Manager" v={query.userName(db, c.rm)} /><Info k="Client Since" v={formatDate(c.createdAt)} />
              </div>
            </Panel>
            <Panel className="p-4">
              <h3 className="font-display text-base font-semibold text-ink">Financial Summary</h3>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-ink-2">Total Billed</span><span className="font-medium" data-testid="client-summary-billed">{financeTotals ? ledgerMoney(financeTotals.billed) : '—'}</span></div>
                <div className="flex justify-between"><span className="text-ink-2">Applied Payments / TDS</span><span className="font-medium" data-testid="client-summary-applied">{financeTotals ? ledgerMoney(financeTotals.billed-financeTotals.outstanding) : '—'}</span></div>
                <div className="flex justify-between"><span className="text-ink-2">Outstanding</span><span className="font-medium text-danger" data-testid="client-summary-outstanding">{financeTotals ? ledgerMoney(financeTotals.outstanding) : '—'}</span></div>
              </div>
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="Matters" className="mt-4">
          {matters.length === 0 ? <EmptyState icon={Users} title="No matters for this client" /> :
            <Panel><div className="divide-y divide-line">{matters.map((m) => (
              <button key={m.id} onClick={() => navigate(`/matters/${m.id}`)} className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface">
                <div><div className="font-medium text-ink">{m.title}</div><div className="text-xs text-ink-2">{m.caseNumber} · {m.court} · {m.stage}</div></div><StatusBadge status={m.status} />
              </button>
            ))}</div></Panel>}
        </TabsContent>

        <TabsContent value="Documents" className="mt-4">
          {docs.length === 0 ? <EmptyState icon={Users} title="No documents" /> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="client-docs-grid">
              {docs.map((d) => (
                <div key={d.id} className="rounded-xl border border-line bg-card p-3" data-testid={`client-doc-${d.id}`}>
                  <button className="w-full" onClick={() => d.storagePath && setPreview(d)}><MediaThumb doc={d} className="h-28" /></button>
                  <div className="mt-2 truncate text-sm font-medium text-ink" title={d.name}>{d.name}</div>
                  <div className="flex items-center justify-between text-xs text-ink-2"><span>{formatDate(d.date)} · {d.size || d.type}</span>
                    {d.storagePath && <a href={`${fileUrl(d.id)}&download=1`} title="Download" data-testid={`client-doc-download-${d.id}`}><Download className="h-4 w-4 hover:text-copper" /></a>}</div>
                </div>
              ))}
            </div>
          )}
          <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
            <DialogContent className="max-w-4xl overflow-hidden p-0">
              {preview && (<>
                <DialogHeader className="px-4 pt-4"><DialogTitle className="truncate font-display text-xl">{preview.name}</DialogTitle></DialogHeader>
                <div className="h-[70vh] bg-ink/90"><MediaPreview doc={preview} /></div>
                <div className="flex justify-end px-4 py-3"><Button size="sm" variant="outline" asChild><a href={`${fileUrl(preview.id)}&download=1`}><Download className="mr-1 h-3.5 w-3.5" />Download</a></Button></div>
              </>)}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="Billing" className="mt-4">
          <FinanceWorkspace clientId={id} compact />
        </TabsContent>

        {["Communication", "Contacts", "Notes", "Timeline"].map((t) => (
          <TabsContent key={t} value={t} className="mt-4">
            <EmptyState icon={Users} title={`${t}`} message={`${t} for ${c.name} will appear here as the relationship develops.`} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
const Info = ({ k, v }) => <div><div className="text-xs text-ink-muted">{k}</div><div className="text-ink">{v}</div></div>;
